import {
  ENROLLMENT_STATUS_LABELS,
  type AutomationDecisionDTO,
  type EnrollmentDTO,
  type EnrollmentStatus,
  type SequenceDetailDTO,
  type SequencePreviewDTO,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Power, Save, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, PageSpinner, Tabs, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';
import { formatNumber } from '../../lib/format';
import { DecisionList } from './DecisionList';
import { EMPTY_SEQUENCE, SequenceForm, type SequenceFormState, bodyFromForm, formFromSequence } from './SequenceForm';

const ENROLLMENT_COLORS: Record<EnrollmentStatus, string> = { active: 'green', paused: 'amber', completed: 'violet', stopped: 'gray' };

export function SequenceDetailPage() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const { data: sequence, isLoading } = useQuery({
    queryKey: ['automation', 'sequences', id],
    queryFn: ({ signal }) => api.get<SequenceDetailDTO>(`/api/sequences/${id}`, { signal }),
    enabled: !isNew,
  });
  if (!isNew && (isLoading || !sequence)) return <PageSpinner />;
  // Keyed so the form state resets when the loaded version changes (after save / activation).
  return <SequenceEditor key={sequence ? `${sequence.id}:${sequence.version}` : 'new'} sequence={sequence ?? null} />;
}

function SequenceEditor({ sequence }: { sequence: SequenceDetailDTO | null }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const manage = can('automation:manage');
  const [form, setForm] = useState<SequenceFormState>(sequence ? formFromSequence(sequence) : EMPTY_SEQUENCE);
  const [tab, setTab] = useState<'preview' | 'enrollments' | 'history'>('preview');
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['automation'] });

  const save = useMutation({
    mutationFn: () =>
      sequence
        ? api.patch<SequenceDetailDTO>(`/api/sequences/${sequence.id}`, { ...bodyFromForm(form), version: sequence.version })
        : api.post<SequenceDetailDTO>('/api/sequences', bodyFromForm(form)),
    onSuccess: async (saved) => {
      toast.success(sequence ? 'Sequence saved' : 'Sequence created — review the preview, then activate it');
      await refresh();
      if (!sequence) navigate(`/automation/sequences/${saved.id}`, { replace: true });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const toggle = useMutation({
    mutationFn: (active: boolean) => api.post<SequenceDetailDTO>(`/api/sequences/${sequence!.id}/${active ? 'activate' : 'deactivate'}`),
    onSuccess: async (saved) => {
      toast.success(saved.isActive ? 'Sequence activated' : 'Sequence deactivated — enrolled leads wait until it is active again');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/sequences/${sequence!.id}`),
    onSuccess: async () => {
      toast.success('Sequence deleted');
      await refresh();
      navigate('/automation');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const { data: preview, isFetching: previewLoading } = useQuery({
    queryKey: ['automation', 'sequences', sequence?.id, 'preview'],
    queryFn: ({ signal }) => api.get<SequencePreviewDTO>(`/api/sequences/${sequence!.id}/preview`, { signal }),
    enabled: Boolean(sequence) && tab === 'preview',
  });
  const { data: enrollments } = useQuery({
    queryKey: ['automation', 'sequences', sequence?.id, 'enrollments'],
    queryFn: ({ signal }) => api.get<{ items: EnrollmentDTO[]; total: number }>(`/api/sequences/${sequence!.id}/enrollments?pageSize=200`, { signal }),
    enabled: Boolean(sequence) && tab === 'enrollments',
  });
  const { data: decisions } = useQuery({
    queryKey: ['automation', 'sequences', sequence?.id, 'decisions'],
    queryFn: ({ signal }) => api.get<{ items: AutomationDecisionDTO[] }>(`/api/automation/decisions?sequenceId=${sequence!.id}&pageSize=100`, { signal }),
    enabled: Boolean(sequence) && tab === 'history',
  });

  const deleteSequence = async () => {
    const live = (sequence?.activeEnrollments ?? 0) + (sequence?.pausedEnrollments ?? 0);
    const ok = await confirm({
      title: 'Delete this sequence?',
      message: live > 0 ? `${live} enrolled leads will be stopped and their unsent emails cancelled.` : 'It has no enrolled leads.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) remove.mutate();
  };

  return (
    <Page width="wide">
      <PageHeader
        title={sequence ? sequence.name : 'New sequence'}
        breadcrumbs={[{ label: 'Automation', to: '/automation' }, { label: sequence ? sequence.name : 'New sequence' }]}
        meta={sequence && <Badge color={sequence.isActive ? 'green' : 'gray'}>{sequence.isActive ? 'Active' : 'Inactive'}</Badge>}
        actions={
          manage ? (
            <div className="flex flex-wrap gap-2">
              {sequence && (
                <Button icon={Trash2} variant="ghost" onClick={() => void deleteSequence()}>
                  Delete
                </Button>
              )}
              {sequence && (
                <Button icon={Power} loading={toggle.isPending} onClick={() => toggle.mutate(!sequence.isActive)}>
                  {sequence.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              )}
              <Button variant="primary" icon={Save} loading={save.isPending} onClick={() => save.mutate()}>
                {sequence ? 'Save changes' : 'Create sequence'}
              </Button>
            </div>
          ) : undefined
        }
      />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <SequenceForm form={form} onChange={setForm} disabled={!manage} />
        {sequence ? (
          <Card className="self-start">
            <Tabs
              tabs={[
                { key: 'preview', label: 'Who it would reach' },
                { key: 'enrollments', label: 'Enrolled', count: sequence.activeEnrollments + sequence.pausedEnrollments },
                { key: 'history', label: 'History' },
              ]}
              value={tab}
              onChange={setTab}
            />
            {tab === 'preview' && (
              <CardBody className="text-[13px]">
                {previewLoading && !preview ? (
                  <p className="text-slate-500">Checking leads…</p>
                ) : preview ? (
                  <>
                    <p className="mb-3 text-slate-700">
                      <strong>{formatNumber(preview.matching)}</strong> pizzerias match the audience right now. Of the first {preview.evaluated} checked,{' '}
                      <strong>{preview.eligible}</strong> would be enrolled on the next run{sequence.autoEnroll ? '' : ' if auto-enroll were on'}.
                      {!sequence.isActive && ' Nothing happens until you activate the sequence.'}
                    </p>
                    <ul className="divide-y divide-slate-100">
                      {preview.sample.map((lead) => (
                        <li key={lead.businessId} className="py-2">
                          <div className="flex items-center gap-2">
                            <Badge color={lead.eligible ? 'green' : 'slate'} size="sm">
                              {lead.eligible ? 'Would enroll' : 'Skipped'}
                            </Badge>
                            <Link to={`/leads/${lead.businessId}`} className="font-medium text-slate-900 hover:text-ember-700">
                              {lead.name}
                            </Link>
                            <span className="text-slate-500">{lead.city}</span>
                            <span className="ml-auto text-xs text-slate-500">{lead.contactEmail ?? 'no contact'}</span>
                          </div>
                          <p className="mt-0.5 text-slate-600">{lead.reason}</p>
                        </li>
                      ))}
                    </ul>
                    {preview.sample.length === 0 && <p className="text-slate-500">No pizzeria matches these criteria right now.</p>}
                  </>
                ) : null}
              </CardBody>
            )}
            {tab === 'enrollments' && (
              <ul className="divide-y divide-slate-100">
                {(enrollments?.items ?? []).map((enrollment) => (
                  <li key={enrollment.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
                    <Badge color={ENROLLMENT_COLORS[enrollment.status]} size="sm">
                      {ENROLLMENT_STATUS_LABELS[enrollment.status]}
                    </Badge>
                    <Link to={`/leads/${enrollment.businessId}`} className="font-medium text-slate-900 hover:text-ember-700">
                      {enrollment.businessName}
                    </Link>
                    <span className="text-slate-500">{enrollment.contactName ?? enrollment.contactEmail}</span>
                    <span className="ml-auto text-slate-600">
                      Step {Math.min(enrollment.currentStep, enrollment.totalSteps)} of {enrollment.totalSteps}
                      {enrollment.nextRunAt && (
                        <>
                          {' '}
                          · next <RelativeTime value={enrollment.nextRunAt} />
                        </>
                      )}
                    </span>
                    {enrollment.statusReason && <span className="basis-full text-xs text-slate-500">{enrollment.statusReason}</span>}
                  </li>
                ))}
                {enrollments && enrollments.items.length === 0 && <li className="px-4 py-6 text-center text-[13px] text-slate-500">No pizzerias enrolled yet.</li>}
              </ul>
            )}
            {tab === 'history' && <DecisionList items={decisions?.items ?? []} showSequence={false} />}
          </Card>
        ) : (
          <Card className="self-start">
            <CardHeader title="Preview" />
            <CardBody className="flex items-center gap-2 text-[13px] text-slate-500">
              <Eye className="size-4" /> Create the sequence to see which pizzerias it would reach. New sequences start inactive.
            </CardBody>
          </Card>
        )}
      </div>
    </Page>
  );
}
